import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useLogout } from 'react-admin'
import { supabase } from '../supabaseClient'
import { Investment, InvestmentWithTenant, InvestmentContextType } from '../types'
import { useTermsConsent } from '../hooks/useTermsConsent'
import { TermsConsentModal } from '../components/TermsConsentModal'

const InvestmentContext = createContext<InvestmentContextType | undefined>(undefined)

interface InvestmentProviderProps {
  children: ReactNode
}

export function InvestmentProvider({ children }: InvestmentProviderProps) {
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [currentInvestment, setCurrentInvestmentState] = useState<InvestmentWithTenant | null>(null)
  const [userInvestments, setUserInvestments] = useState<InvestmentWithTenant[]>([])
  const [loading, setLoading] = useState(true)

  const termsConsent = useTermsConsent(currentUser)

  // Load user's investments on auth state change
  useEffect(() => {
    // Get initial user
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUser(user)
      if (user) {
        loadUserInvestments(user.id)
      } else {
        setCurrentInvestmentState(null)
        setUserInvestments([])
        setLoading(false)
      }
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const user = session?.user || null
        setCurrentUser(user)
        
        if (user) {
          loadUserInvestments(user.id)
        } else {
          setCurrentInvestmentState(null)
          setUserInvestments([])
          setLoading(false)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const loadUserInvestments = async (userId: string) => {
    if (!userId) return

    console.log('🔍 Loading investments for user:', userId)

    try {
      setLoading(true)

      // Get tenants through user_tenant_permissions table
      const { data: userPermissions, error: permissionsError } = await supabase
        .from('user_tenant_permissions')
        .select(`
          tenant_id,
          tenant:tenants(*)
        `)
        .eq('user_id', userId)

      if (permissionsError) throw permissionsError

      console.log('📋 User permissions:', userPermissions)

      if (!userPermissions || userPermissions.length === 0) {
        // User has no permissions - no investments available
        console.log('❌ No permissions found for user')
        setUserInvestments([])
        setCurrentInvestmentState(null)
        setLoading(false)
        return
      }

      const tenantIds = userPermissions.map(p => p.tenant_id)

      // Get investments for user's permitted tenants
      const { data: investments, error: investmentError } = await supabase
        .from('investments')
        .select('*, tenant:tenants(*)')
        .in('tenant_id', tenantIds)
        .order('name')

      if (investmentError) throw investmentError

      console.log('🏢 Found investments:', investments)

      const list = (investments as InvestmentWithTenant[]) || []
      setUserInvestments(list)

      // Set default investment if none is selected
      if (!currentInvestment && list.length > 0) {
        // Try to load from localStorage first
        const savedInvestmentId = localStorage.getItem('currentInvestmentId')
        const savedInvestment = savedInvestmentId
          ? list.find(inv => inv.id === savedInvestmentId)
          : null

        setCurrentInvestmentState(savedInvestment || list[0])
      }
    } catch (error) {
      console.error('Error loading user investments:', error)
    } finally {
      setLoading(false)
    }
  }

  const setCurrentInvestment = (investment: Investment) => {
    setCurrentInvestmentState(investment)
    // Persist to localStorage
    localStorage.setItem('currentInvestmentId', investment.id)
  }

  // Patch inwestycji w pamięci po zapisie — bez tego switch i powrót pokazują stary obiekt
  // (kontekst ładowany raz przy logowaniu), świeże dane dopiero po przeładowaniu strony.
  const patchInvestment = (id: string, patch: Partial<Investment>) => {
    setUserInvestments(list => list.map(inv => (inv.id === id ? { ...inv, ...patch } : inv)))
    setCurrentInvestmentState(curr => (curr && curr.id === id ? { ...curr, ...patch } : curr))
  }

  const contextValue: InvestmentContextType = {
    currentInvestment,
    userInvestments,
    setCurrentInvestment,
    patchInvestment,
    loading
  }

  const logout = useLogout()

  const handleLogout = () => logout()

  return (
    <InvestmentContext.Provider value={contextValue}>
      {children}
      {!termsConsent.loading && termsConsent.requiresConsent && termsConsent.termsVersion && (
        <TermsConsentModal
          termsVersion={termsConsent.termsVersion}
          onAccept={termsConsent.accept}
          onLogout={handleLogout}
        />
      )}
    </InvestmentContext.Provider>
  )
}

export function useInvestmentContext(): InvestmentContextType {
  const context = useContext(InvestmentContext)
  if (context === undefined) {
    throw new Error('useInvestmentContext must be used within an InvestmentProvider')
  }
  return context
}